import { gql, useMutation, useQuery } from "@apollo/client";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useState } from "react";

import { useCurrentAccountId } from "@zapier/identity";

import { getErrorMessage } from "@zapier/reporting/src/utils";
import { getChannelMessage } from "./messageBuilder";
import { MassActionType } from "./types";
import {
  ChannelRollupQuery,
  DeleteRunsMutation,
  ReplayRunsMutation,
} from "./useMassAction.generated";

const runActionResultFragment = gql`
  fragment RunActionMutationResult on RunActionResult {
    channel
    failures
    globalFailures
    globalFailureType
    isMassActionsDisabled
    pending
  }
`;

const channelRollupQuery = gql`
  query ChannelRollup($accountId: ID!, $channelId: ID!) {
    channelRollup(accountId: $accountId, id: $channelId) {
      id
      statuses {
        count
        status
      }
      total
    }
  }
`;

export const deleteRunsMutation = gql`
  mutation DeleteRuns($accountId: ID!, $filters: RunFilter!, $runIds: [ID!]) {
    deleteRuns(accountId: $accountId, filters: $filters, runIds: $runIds) {
      ...RunActionMutationResult
    }
  }

  ${runActionResultFragment}
`;

export const replayRunsMutation = gql`
  mutation ReplayRuns($accountId: ID!, $filters: RunFilter!, $runIds: [ID!]) {
    replayRuns(accountId: $accountId, filters: $filters, runIds: $runIds) {
      ...RunActionMutationResult
    }
  }

  ${runActionResultFragment}
`;

type RunningChannel = {
  id: string;
  onComplete: (refetch: boolean) => void;
  type: MassActionType;
};

function hasError(results: any) {
  if (results?.error) return true;

  return Boolean(
    results?.data?.replayRuns?.globalFailures ||
      results?.data?.deleteRuns?.globalFailures
  );
}

function getHasPending(
  type: MassActionType | undefined,
  deletePending: number | undefined = 0,
  replayPending: number | undefined = 0
) {
  if (type === "replay") {
    return replayPending > 0;
  } else if (type === "delete") {
    return deletePending > 0;
  }
  return false;
}

export function useMassAction() {
  const t = useTranslations("mass-action");
  const accountId = useCurrentAccountId();
  const [runningChannel, setRunningChannel] = useState<RunningChannel | null>(
    null
  );

  const [deleteRuns, deleteRunsResult] =
    useMutation<DeleteRunsMutation>(deleteRunsMutation);

  const [replayRuns, replayRunsResult] =
    useMutation<ReplayRunsMutation>(replayRunsMutation);

  const hasPendingFromChannelCreation = getHasPending(
    runningChannel?.type,
    deleteRunsResult?.data?.deleteRuns?.pending,
    replayRunsResult?.data?.replayRuns?.pending
  );

  const queryResults = useQuery<ChannelRollupQuery>(channelRollupQuery, {
    skip: !accountId || !runningChannel?.id || !hasPendingFromChannelCreation,
    variables: {
      accountId,
      channelId: runningChannel?.id,
    },
  });

  const rollup = queryResults?.data?.channelRollup;

  const hasPendingStatus = Boolean(
    rollup?.statuses?.find((s) => s.status === "pending")
  );

  useEffect(() => {
    if (hasPendingFromChannelCreation) {
      // New channel with pending items so start polling
      queryResults.startPolling(3000);
    } else if (runningChannel) {
      // New channel without any pending items
      if (
        (runningChannel.type === "replay" && hasError(replayRunsResult)) ||
        (runningChannel.type === "delete" && hasError(deleteRunsResult))
      ) {
        // New channel where channel creation errored for some reason so
        // call on complete without refetching
        runningChannel.onComplete(false);
      } else {
        // All of the actions were completed during channel creation
        // so mark as complete and trigger refetch
        runningChannel.onComplete(true);
      }
      queryResults.stopPolling();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runningChannel]);

  useEffect(() => {
    if (!queryResults.loading && !hasPendingStatus) {
      // Pending status changed to false so stop polling
      queryResults.stopPolling();
      if (runningChannel) {
        // Notify of complete status
        runningChannel.onComplete(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPendingStatus, queryResults.loading]);

  const onMassAction = useCallback(
    async function (
      type: MassActionType,
      variables: Record<string, any>,
      onComplete: (refetch: boolean) => void
    ) {
      if (type === "delete") {
        // kick off delete
        const results = await deleteRuns({
          variables: {
            accountId,
            ...variables,
          },
        });
        // Save channel info
        const id = results?.data?.deleteRuns?.channel || "deleteError";
        setRunningChannel({ id, onComplete, type });
      } else if (type === "replay") {
        const { zapId, ...restOfVariables } = variables;

        // kick off replay
        const results = await replayRuns({
          variables: {
            accountId,
            ...restOfVariables,
          },
        });
        // Save channel info
        const id = results?.data?.replayRuns?.channel || "replayError";
        setRunningChannel({ id, onComplete, type });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deleteRuns, replayRuns, setRunningChannel]
  );

  const message = getChannelMessage({
    channel:
      runningChannel?.type === "delete"
        ? deleteRunsResult?.data?.deleteRuns
        : replayRunsResult?.data?.replayRuns,
    id: runningChannel?.id,
    statusResult: rollup,
    t,
    type: runningChannel?.type,
  });
  const id = runningChannel?.id || "new";

  return {
    actionError: getErrorMessage(
      deleteRunsResult.error ?? replayRunsResult.error
    ),
    isLoading:
      queryResults.loading ||
      deleteRunsResult.loading ||
      replayRunsResult.loading,
    isRunning: hasPendingStatus,
    message: message ? { ...message, id } : null,
    onMassAction,
  };
}
